/**
 * PositionedMetricSelectorView - Positioned metric selector around radar chart
 *
 * Displays a metric selector button positioned around the radar chart.
 * @remarks This is a View component - contains no business logic, hooks, or state
 */

import React from "react";
import { type LayoutChangeEvent } from "react-native";

import { Box } from "@/components/atoms";
import MetricSelector from "@/features/player-detail/components/molecules/MetricSelector";

import { TEST_ID_PLAYER_DETAIL } from "e2e/testId";

/**
 * Metric option for the selector
 */
interface MetricOption {
  readonly label: string;
  readonly value: number | null;
}

/**
 * Container position style with numeric top/left and optional web-only CSS transform
 */
interface SelectorPositionStyle {
  readonly top: number;
  readonly left: number;
  readonly transform?: string;
}

/**
 * Props for PositionedMetricSelectorView component
 */
interface PositionedMetricSelectorViewProps {
  /**
   * Stable axis position (0-5). Used as the testID suffix so selectors
   * stay deterministic under copy/i18n changes to `metric.label`.
   */
  readonly position: number;
  readonly metric: MetricOption;
  readonly availableMetrics: readonly MetricOption[];
  readonly isDark: boolean;
  readonly containerStyle: SelectorPositionStyle;
  readonly onMetricChange: (metric: MetricOption) => void;
  /** Layout measurement callback for native centering */
  readonly onLayout?: (event: LayoutChangeEvent) => void;
}

/**
 * PositionedMetricSelectorView renders a metric selector at a calculated position
 * @param props - Component properties
 * @param props.position - Stable axis position (0-5) used as testID suffix
 * @param props.metric - Selected metric for this position
 * @param props.availableMetrics - All available metric options
 * @param props.isDark - Whether dark mode is active
 * @param props.containerStyle - Calculated position style (numeric top/left, optional transform)
 * @param props.onMetricChange - Handler for metric selection change
 * @param props.onLayout - Layout callback for measuring selector dimensions on native
 * @returns Box containing the positioned metric selector
 */
const PositionedMetricSelectorView = ({
  position,
  metric,
  availableMetrics,
  isDark,
  containerStyle,
  onMetricChange,
  onLayout,
}: PositionedMetricSelectorViewProps) => (
  <Box
    testID={`${TEST_ID_PLAYER_DETAIL.METRIC_SELECTOR_PREFIX}-${position}`}
    className="absolute z-10"
    // eslint-disable-next-line no-restricted-syntax -- computed selector position: data-driven numeric top/left (chart geometry) + optional web CSS transform string; not enum-reducible to a closed token
    UNSAFE_style={containerStyle}
    onLayout={onLayout}
  >
    <MetricSelector
      selectedMetric={metric}
      options={availableMetrics}
      onSelect={onMetricChange}
      isDark={isDark}
    />
  </Box>
);

PositionedMetricSelectorView.displayName = "PositionedMetricSelectorView";

export default React.memo(PositionedMetricSelectorView);
